import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
    ArrowRightIcon,
    CheckCircleIcon,
    ExternalLinkIcon,
    ShieldCheckIcon,
} from '@heroicons/vue/solid';

import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';
import { useUiStore } from '@/stores';
import { MetaIntegrationService } from '@/services';
import {
    launchMetaEmbeddedSignup,
    loadMetaSdk,
    MetaSdk,
    MetaSignupMode,
} from '@/services/metaEmbeddedSignup';
import { toast, toastServiceError } from '@/services/core/notification';
import { MetaConnectionStatus, MetaConnectionStatusResult, Permission } from '@/types';
import WhatsAppAssetStatusPanel from './components/WhatsAppAssetStatusPanel.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsAppMetaConnectPage',
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();
const uiStore = useUiStore();
const router = useRouter();

const status: Ref<MetaConnectionStatus> = ref(null);
const connection = ref<MetaConnectionStatusResult | null>(null);
const isSignupInProgress = ref(false);
const signupMode = ref<MetaSignupMode>('EXISTING_WABA');

const signupModes: MetaSignupMode[] = ['EXISTING_WABA', 'NEW_NUMBER'];
const comingSoonModes = ['coexistence', 'migration'];

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const isConnected = computed(() => status.value === 'connected');
const connectionButtonLabel = computed(() =>
    isConnected.value
        ? t('whatsappMeta.connect.reconnectButton')
        : t('whatsappMeta.connect.button'),
);

const onConnectClick = async () => {
    if (isSignupInProgress.value) return;

    isSignupInProgress.value = true;
    setLoading(true);
    try {
        const { data: embeddedSignup } = await MetaIntegrationService.startWhatsAppConnect();
        const metaSdk: MetaSdk = await loadMetaSdk(embeddedSignup.appId);
        setLoading(false);
        const result = await launchMetaEmbeddedSignup(
            metaSdk,
            embeddedSignup.configId,
            signupMode.value,
        );
        setLoading(true);
        const { data } = await MetaIntegrationService.completeWhatsAppConnect({
            state: embeddedSignup.state,
            ...result,
        });
        connection.value = data;
        status.value = data.status;
        if (
            data.wabaId &&
            data.migrationRequired &&
            window.confirm(t('whatsappMeta.connect.migrationConfirmation'))
        ) {
            const migration = await MetaIntegrationService.migrateWaba(data.wabaId);
            connection.value = migration.data;
            status.value = migration.data.status;
        }
        if (data.wabaId) {
            await router.replace({ name: 'WhatsAppMetaDetail', params: { wabaId: data.wabaId } });
            return;
        }
        toast.success(t('whatsappMeta.connect.previewReady'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        isSignupInProgress.value = false;
    }
};

const onConfirmConnection = async (connectionId: string) => {
    setLoading(true);
    try {
        const { data } = await MetaIntegrationService.confirmWhatsAppConnect(connectionId);
        connection.value = data;
        status.value = data.status;
        toast.success(t('whatsappMeta.connect.confirmed'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const __returned__ = { t, uiStore, router, status, connection, isSignupInProgress, signupMode, signupModes, comingSoonModes, setLoading, isConnected, connectionButtonLabel, onConnectClick, onConfirmConnection, get ArrowRightIcon() { return ArrowRightIcon }, get CheckCircleIcon() { return CheckCircleIcon }, get ExternalLinkIcon() { return ExternalLinkIcon }, get ShieldCheckIcon() { return ShieldCheckIcon }, SVGWhatsApp, get Permission() { return Permission }, WhatsAppAssetStatusPanel }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})